import {Component, inject, signal, DestroyRef} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Capability} from '../../../../classes/models/employee/Capability';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {EmployeeCapabilityService} from '../../../../services/http/employee/employee-capability.service';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

export interface CapabilityDeleteData {
  capability: Capability;
  employeePositionId?: EmployeePositionId;
}

/**
 * This component handles deleting employee's capabilities.
 */
@Component({
  selector: 'app-capability-delete',
  templateUrl: './capability-delete.component.html',
  styleUrls: ['./capability-delete.component.less'],
  imports: [SHARED_IMPORTS]
})
export class CapabilityDeleteComponent {
  /** A capability to be deleted. */
  readonly capability = signal<Capability>(undefined);
  /** Identifiers of an employee and position. */
  readonly employeePositionId = signal<EmployeePositionId>(undefined);
  /** `True` if request sending to backend is in progress. */
  loading = false;
  private employeeCapabilityService = inject(EmployeeCapabilityService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }

  static open(modalService: NgbModal, data: CapabilityDeleteData): NgbModalRef {
    const modalRef = modalService.open(CapabilityDeleteComponent);
    const instance = modalRef.componentInstance as CapabilityDeleteComponent;
    instance.capability.set(data.capability);
    instance.employeePositionId.set(data.employeePositionId);
    return modalRef;
  }

  /**
   * Sends request to backend to delete a capability.
   */
  confirm(modalComponent: ModalComponent): void {
    this.employeeCapabilityService.deleteCapability(this.employeePositionId(), this.capability().startDate)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading = false;
                this.toastr.success('Capabilities successfully deleted');
                this.ruleCheckerService.check();
              },
              error: error => {
                this.loading = false;
                this.toastr.error(error.error.message);
              }
            });
  }
}